import { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CLOSE_KINDS, CloseKind } from '../../api/issues';
import { Modal } from '../Modal/Modal';

export interface CloseChoice {
    kind: CloseKind;
    note: string;
}

interface CloseRequest {
    title: string;
    detail?: string;
}

/**
 * Asks why something is closed: a fixed choice, so the scanner can learn from it, and an
 * optional note. `ask` resolves with the choice, or null when the person changed their mind.
 */
export const useCloseDialog = () => {
    const [request, setRequest] = useState<CloseRequest | null>(null);
    const [kind, setKind] = useState<CloseKind>('not-relevant');
    const [note, setNote] = useState<string>('');
    const resolver = useRef<((choice: CloseChoice | null) => void) | null>(null);

    const ask = useCallback(
        (title: string, detail?: string) =>
            new Promise<CloseChoice | null>((resolve) => {
                resolver.current?.(null);
                resolver.current = resolve;
                setKind('not-relevant');
                setNote('');
                setRequest({ title, detail });
            }),
        []
    );

    const finish = (choice: CloseChoice | null) => {
        resolver.current?.(choice);
        resolver.current = null;
        setRequest(null);
    };

    const content = (
        <div className="close-dialog">
            {request?.detail && <p className="close-dialog__detail">{request.detail}</p>}
            <fieldset>
                <legend>Waarom?</legend>
                {(Object.keys(CLOSE_KINDS) as CloseKind[]).map((value) => (
                    <label key={value} className="close-dialog__choice">
                        <input type="radio" name="close-kind" value={value} checked={kind === value} onChange={() => setKind(value)} /> {CLOSE_KINDS[value]}
                    </label>
                ))}
            </fieldset>
            <label className="close-dialog__note">
                <span>Toelichting (optioneel)</span>
                <input
                    className="form-control form-control-sm"
                    type="text"
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Bijvoorbeeld: factuur is gecrediteerd"
                    onKeyDown={(event) => event.key === 'Enter' && finish({ kind, note })}
                />
            </label>
            <p className="issues__muted">Het issue gaat vanzelf weer open als het object opnieuw faalt.</p>
        </div>
    );

    const footer = (
        <div className="d-flex gap-2 ms-auto">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => finish(null)}>
                Annuleren
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => finish({ kind, note })}>
                <FontAwesomeIcon icon="box-archive" /> Sluiten
            </button>
        </div>
    );

    const dialog = <Modal show={request !== null} handleClose={() => finish(null)} title={request?.title ?? 'Sluiten'} content={content} footer={footer} showCancel={false} />;

    return { dialog, ask };
};
